import { useMemo, useState, useEffect } from "react";
import { TrendingUp, TrendingDown, AlertTriangle, Edit, Save, X, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCurrency } from "@/hooks/useCurrency";
import { budgetService } from "@/services/budget.service";
import { weddingService } from "@/services/wedding.service";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { permissions } from "@/lib/mockAuth";
import AddMiscExpenseDialog from "./AddMiscExpenseDialog";

export const BudgetModule = ({ weddingId, wedding, onUpdateWedding, onBudgetUpdate }) => {
  const { userRole, activeOrg } = useAuth();
  const { formatCurrency } = useCurrency();
  const canManage = permissions.canManageBudget(userRole);
  const isReadOnly = permissions.isReadOnly(userRole);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [editBudget, setEditBudget] = useState('');
  const [isMiscDialogOpen, setIsMiscDialogOpen] = useState(false);
  const [viewFilter, setViewFilter] = useState('all'); // 'all', 'vendors', 'misc'
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [hoveredSlice, setHoveredSlice] = useState(null);

  useEffect(() => {
    const loadBudgetCategories = async () => {
      if (!weddingId || !activeOrg?.id) return;
      
      try {
        setLoading(true);
        const budgetCategories = await budgetService.getBudgetCategories(weddingId, activeOrg.id);
        setCategories(budgetCategories);
      } catch (error) {
        console.error('Failed to load budget categories:', error);
        toast.error('Failed to load budget categories');
      } finally {
        setLoading(false);
      }
    };

    loadBudgetCategories();
  }, [weddingId, activeOrg]);

  // Calculate total spent from budget categories (real-time data)
  const totalSpent = categories.reduce((sum, cat) => sum + (cat.spent || 0), 0);
  const budgetPercentage = (totalSpent / wedding.budget) * 100;
  const remainingBudget = wedding.budget - totalSpent;

  const expenseBreakdown = useMemo(() => {
    const map = new Map(); // label -> amount
    for (const c of categories) {
      const spent = Number(c.spent || 0);
      if (!spent || spent <= 0) continue;

      const isMisc = c.name === "Miscellaneous";
      const label = isMisc ? (c.vendor || "Others") : (c.name || "Others");
      map.set(label, (map.get(label) || 0) + spent);
    }

    const items = Array.from(map.entries())
      .map(([label, amount]) => ({ label, amount }))
      .sort((a, b) => b.amount - a.amount);

    const total = items.reduce((s, i) => s + i.amount, 0);
    return { items, total };
  }, [categories]);

  const pieSlices = useMemo(() => {
    const palette = [
      "#f97316", // orange
      "#ec4899", // pink
      "#8b5cf6", // violet
      "#22c55e", // green
      "#06b6d4", // cyan
      "#eab308", // yellow
      "#3b82f6", // blue
      "#ef4444", // red
      "#14b8a6", // teal
      "#a855f7", // purple
    ];

    const total = expenseBreakdown.total || 0;
    if (total <= 0) return [];

    // If too many segments, group the tail into "Others"
    const maxSegments = 8;
    let items = expenseBreakdown.items;
    if (items.length > maxSegments) {
      const head = items.slice(0, maxSegments - 1);
      const tail = items.slice(maxSegments - 1);
      const othersAmount = tail.reduce((s, i) => s + i.amount, 0);
      items = [...head, { label: "Others", amount: othersAmount }];
    }

    const cx = 120;
    const cy = 120;
    const r = 92;

    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      };
    };

    const describeArc = (x, y, radius, startAngle, endAngle) => {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return [
        "M",
        start.x,
        start.y,
        "A",
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
        "L",
        x,
        y,
        "Z",
      ].join(" ");
    };

    let currentAngle = 0;
    return items.map((item, idx) => {
      const pct = (item.amount / total) * 100;
      const sweep = (item.amount / total) * 360;
      const start = currentAngle;
      const end = currentAngle + sweep;
      currentAngle = end;
      return {
        ...item,
        pct,
        color: palette[idx % palette.length],
        d: describeArc(cx, cy, r, start, end),
      };
    });
  }, [expenseBreakdown]);

  const handleEditBudgetClick = () => {
    setIsEditingBudget(true);
    setEditBudget(wedding.budget.toString());
  };

  const handleSaveBudget = async () => {
    try {
      const newBudget = parseInt(editBudget);
      if (isNaN(newBudget) || newBudget <= 0) {
        toast.error('Please enter a valid budget amount');
        return;
      }

      await weddingService.updateWedding(weddingId, { budget: newBudget });
      
      if (onUpdateWedding) {
        onUpdateWedding({ ...wedding, budget: newBudget });
      }
      
      setIsEditingBudget(false);
      toast.success('Wedding budget updated successfully');
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Failed to update budget:', error);
      toast.error('Failed to update budget');
    }
  };

  const handleCancelBudget = () => {
    setIsEditingBudget(false);
    setEditBudget('');
  };

  // Separate vendor categories and miscellaneous expenses
  const vendorCategories = categories.filter(cat => cat.name !== 'Miscellaneous');
  const miscExpenses = categories.filter(cat => cat.name === 'Miscellaneous');

  const handleAddMiscExpense = async (expenseData) => {
    try {
      await budgetService.createBudgetCategory(weddingId, activeOrg.id, {
        name: 'Miscellaneous',
        allocated: expenseData.allocated,
        spent: expenseData.spent,
        vendor: expenseData.name // Store description in vendor field for display
      });
      
      // Reload categories
      const budgetCategories = await budgetService.getBudgetCategories(weddingId, activeOrg.id);
      setCategories(budgetCategories);
      
      // Notify parent to refresh budget data
      if (onBudgetUpdate) {
        onBudgetUpdate();
      }
      
      toast.success('Miscellaneous expense added successfully');
    } catch (error) {
      console.error('Failed to add miscellaneous expense:', error);
      toast.error('Failed to add miscellaneous expense');
    }
  };

  const handleDeleteMiscExpense = async () => {
    if (!deletingExpense) return;
    
    const expense = categories.find(c => c.id === deletingExpense);
    const hasSpent = expense && expense.spent && expense.spent > 0;
    
    try {
      await budgetService.deleteBudgetCategory(deletingExpense);
      
      // Reload categories
      const budgetCategories = await budgetService.getBudgetCategories(weddingId, activeOrg.id);
      setCategories(budgetCategories);
      
      // Notify parent to refresh budget data
      if (onBudgetUpdate) {
        onBudgetUpdate();
      }
      
      if (hasSpent) {
        toast.warning(
          `Expense "${expense?.vendor || 'Miscellaneous'}" deleted. ₹${formatCurrency(expense.spent)} in spent amount was removed from budget.`,
          { duration: 5000 }
        );
      } else {
        toast.success('Miscellaneous expense deleted successfully');
      }
      
      setDeletingExpense(null);
    } catch (error) {
      console.error('Failed to delete miscellaneous expense:', error);
      toast.error('Failed to delete miscellaneous expense');
    }
  };

  return (
    <div>
      {/* Overview Card */}
      <Card className="p-6 mb-6 border-t-4 border-t-primary">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-foreground">Budget Overview</h3>
          {canManage && !isEditingBudget && (
            <Button size="sm" variant="outline" onClick={handleEditBudgetClick}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Budget
            </Button>
          )}
        </div>
        
        {isEditingBudget ? (
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Total Wedding Budget (₹)</label>
              <Input
                type="number"
                value={editBudget}
                onChange={(e) => setEditBudget(e.target.value)}
                className="mb-3"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveBudget} className="gradient-primary">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelBudget}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Budget</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(wedding.budget)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Spent</p>
              <p className="text-2xl font-bold text-accent">{formatCurrency(totalSpent)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Remaining</p>
              <p className="text-2xl font-bold text-success">{formatCurrency(remainingBudget)}</p>
            </div>
          </div>
        )}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Overall Progress</span>
            <span className="text-sm font-semibold text-foreground">{budgetPercentage.toFixed(1)}%</span>
          </div>
          <Progress value={budgetPercentage} className="h-3" />
        </div>

        {budgetPercentage > 80 && (
          <Alert className="mt-4 border-warning bg-warning/10">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertDescription className="text-warning">
              You've used {budgetPercentage.toFixed(1)}% of your budget. Consider reviewing expenses.
            </AlertDescription>
          </Alert>
        )}
      </Card>

      {/* Expenses Breakdown Pie Chart */}
      <Card className="p-6 mb-6">
        {expenseBreakdown.total <= 0 ? (
          <div className="text-sm text-muted-foreground">No expenses yet to show a breakdown.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="relative flex justify-center lg:justify-start">
              {/* Hover tooltip */}
              {hoveredSlice && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 lg:left-6 lg:translate-x-0 bg-slate-900 text-white px-3 py-2 rounded-md text-sm shadow-lg border border-slate-700">
                  <div className="font-semibold">{hoveredSlice.label}</div>
                  <div className="text-xs text-slate-200">
                    {hoveredSlice.pct.toFixed(1)}% • {formatCurrency(hoveredSlice.amount)}
                  </div>
                </div>
              )}

              <div className="bg-muted/30 rounded-2xl p-6">
                <svg width="240" height="240" viewBox="0 0 240 240" role="img" aria-label="Expenses pie chart">
                  {/* Background ring */}
                  <circle cx="120" cy="120" r="92" fill="#ffffff00" stroke="rgba(148,163,184,0.25)" strokeWidth="18" />
                  {pieSlices.map((s) => (
                    <path
                      key={s.label}
                      d={s.d}
                      fill={s.color}
                      opacity={hoveredSlice && hoveredSlice.label !== s.label ? 0.55 : 1}
                      onMouseEnter={() => setHoveredSlice(s)}
                      onMouseLeave={() => setHoveredSlice(null)}
                      style={{ cursor: "pointer" }}
                    >
                      <title>{`${s.label} • ${s.pct.toFixed(1)}% • ${formatCurrency(s.amount)}`}</title>
                    </path>
                  ))}
                  {/* Center cutout */}
                  <circle cx="120" cy="120" r="56" fill="#ffffff" stroke="rgba(148,163,184,0.35)" strokeWidth="1" />
                  <text x="120" y="112" textAnchor="middle" fontSize="12" fill="rgba(100,116,139,1)">
                    Total Spent
                  </text>
                  <text x="120" y="138" textAnchor="middle" fontSize="18" fontWeight="700" fill="rgba(15,23,42,1)">
                    {formatCurrency(expenseBreakdown.total)}
                  </text>
                </svg>
              </div>
            </div>

            {/* Legend */}
            <div className="space-y-3">
              {pieSlices.map((s) => (
                <div key={s.label} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="h-3.5 w-3.5 rounded-sm shrink-0"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="text-sm text-foreground truncate">{s.label}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{s.pct.toFixed(1)}%</span>
                  </div>
                  <div className="text-sm font-medium text-foreground shrink-0">{formatCurrency(s.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Expenses Section with Filter */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Expenses</h3>
          {canManage && viewFilter === 'misc' && (
            <Button onClick={() => setIsMiscDialogOpen(true)} size="sm" className="gradient-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add Misc Expense
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setViewFilter('all')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              viewFilter === 'all'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            All Expenses ({categories.length})
          </button>
          <button
            onClick={() => setViewFilter('vendors')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              viewFilter === 'vendors'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Vendor Expenses ({vendorCategories.length})
          </button>
          <button
            onClick={() => setViewFilter('misc')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              viewFilter === 'misc'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Miscellaneous ({miscExpenses.length})
          </button>
        </div>
        {loading ? (
          <Card className="p-6">
            <div className="text-center text-muted-foreground">Loading expenses...</div>
          </Card>
        ) : (() => {
          // Determine which categories to show based on filter
          let displayCategories = [];
          if (viewFilter === 'all') {
            displayCategories = categories.filter(cat => cat.spent > 0);
          } else if (viewFilter === 'vendors') {
            displayCategories = vendorCategories.filter(cat => cat.spent > 0);
          } else if (viewFilter === 'misc') {
            displayCategories = miscExpenses;
          }

          if (displayCategories.length === 0) {
            return (
              <Card className="p-6">
                <div className="text-center text-muted-foreground">
                  {viewFilter === 'misc' 
                    ? 'No miscellaneous expenses added yet' 
                    : 'No expenses found'}
                </div>
              </Card>
            );
          }

          return displayCategories.map((category) => {
          const categoryPercentage = (category.spent / category.allocated) * 100;
          
          const isMiscExpense = category.name === 'Miscellaneous';
          
          return (
            <Card key={category.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">
                      {isMiscExpense ? (category.vendor || 'Miscellaneous') : category.name}
                    </h4>
                    {!isMiscExpense && category.vendor && (
                      <p className="text-sm text-muted-foreground">Vendor: {category.vendor}</p>
                    )}
                  </div>
                  {isMiscExpense && canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingExpense(category.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* View Mode - Categories are read-only */}
                <>
                  {isMiscExpense ? (
                    /* Simplified view for miscellaneous expenses */
                    <div>
                      <div>
                        <p className="text-xs text-muted-foreground">Amount</p>
                        <p className="text-lg font-medium text-accent">{formatCurrency(category.spent)}</p>
                      </div>
                    </div>
                  ) : (
                    /* Full view for vendor categories */
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Allocated</p>
                          <p className="text-sm font-medium">{formatCurrency(category.allocated)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Spent</p>
                          <p className="text-sm font-medium text-accent">{formatCurrency(category.spent)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Remaining</p>
                          <p className="text-sm font-medium text-success">
                            {formatCurrency(category.allocated - category.spent)}
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Progress</span>
                          <span className="text-xs font-medium">{categoryPercentage.toFixed(0)}%</span>
                        </div>
                        <Progress value={categoryPercentage} className="h-2" />
                      </div>

                      {/* Status Indicator */}
                      {categoryPercentage >= 100 ? (
                        <div className="flex items-center gap-2 text-xs text-destructive">
                          <TrendingUp className="h-3 w-3" />
                          <span>Over budget</span>
                        </div>
                      ) : categoryPercentage > 80 ? (
                        <div className="flex items-center gap-2 text-xs text-warning">
                          <AlertTriangle className="h-3 w-3" />
                          <span>Near limit</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-success">
                          <TrendingDown className="h-3 w-3" />
                          <span>On track</span>
                        </div>
                      )}
                    </>
                  )}
                </>
              </div>
            </Card>
          );
        });
        })()}
      </div>

      {/* Add Miscellaneous Expense Dialog */}
      <AddMiscExpenseDialog
        open={isMiscDialogOpen}
        onOpenChange={setIsMiscDialogOpen}
        onAddExpense={handleAddMiscExpense}
      />

      {/* Delete Expense Confirmation */}
      <AlertDialog open={!!deletingExpense} onOpenChange={() => setDeletingExpense(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const expense = categories.find(c => c.id === deletingExpense);
                const hasSpent = expense && expense.spent && expense.spent > 0;
                return hasSpent ? "⚠️ Warning: Expense Has Spent Amount" : "Delete Expense?";
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {(() => {
                const expense = categories.find(c => c.id === deletingExpense);
                if (!expense) return null;
                
                const hasSpent = expense.spent && expense.spent > 0;
                const expenseName = expense.vendor || 'Miscellaneous';
                
                if (hasSpent) {
                  return (
                    <>
                      <p>
                        You are about to delete <strong>{expenseName}</strong>. This expense has <strong>₹{formatCurrency(expense.spent)}</strong> in spent amount.
                      </p>
                      <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
                        <p className="text-sm font-medium text-warning-foreground mb-2">
                          ⚠️ Impact:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                          <li>₹{formatCurrency(expense.spent)} will be removed from total spent</li>
                          <li>Budget calculations will be updated</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground pt-2">
                        Are you sure you want to proceed?
                      </p>
                    </>
                  );
                } else {
                  return (
                    <p>
                      Are you sure you want to delete <strong>{expenseName}</strong>? This action cannot be undone.
                    </p>
                  );
                }
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMiscExpense}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(() => {
                const expense = categories.find(c => c.id === deletingExpense);
                const hasSpent = expense && expense.spent && expense.spent > 0;
                return hasSpent ? "Delete Anyway" : "Delete";
              })()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BudgetModule;
